// Working-day counting for leave balances (client mirror of
// HR-backend/utils/leaveDays.js — keep the two in sync, identical logic).
//
// A leave only consumes WORKING days: Monday–Friday, excluding company holidays.
// Weekends (Sat/Sun) and holidays inside the range are NOT charged. All calendar
// math is done in Pakistan Standard Time (UTC+5, no DST) so the count matches the
// backend exactly. The core counter works purely on 'YYYY-MM-DD' strings.

const PKT_OFFSET_MS = 5 * 60 * 60 * 1000; // UTC+5, no daylight saving
const DAY_MS = 24 * 60 * 60 * 1000;
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function ymdFromUtcMs(ms) {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Any Date / ISO string -> the PKT calendar date as 'YYYY-MM-DD' (or null if invalid).
export function toPktDateStr(value) {
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return ymdFromUtcMs(d.getTime() + PKT_OFFSET_MS);
}

export function normalizeYMD(value) {
  if (typeof value === "string" && YMD_RE.test(value)) return value;
  return toPktDateStr(value);
}

// The ISO instant for PKT-midnight of a given date, INDEPENDENT of the caller's
// timezone. Use this when SUBMITTING a picked date so storage never depends on the
// browser's clock. Round-trips exactly: toPktDateStr(pktMidnightISO(d)) === d.
export function pktMidnightISO(value) {
  const ymd = normalizeYMD(value);
  if (!ymd) return null;
  return new Date(utcMidnightMs(ymd) - PKT_OFFSET_MS).toISOString();
}

function utcMidnightMs(ymd) {
  return Date.UTC(
    Number(ymd.slice(0, 4)),
    Number(ymd.slice(5, 7)) - 1,
    Number(ymd.slice(8, 10))
  );
}

// Build a Set of holiday date-strings ('YYYY-MM-DD') from holiday range docs.
export function buildHolidaySet(holidays) {
  const set = new Set();
  for (const h of holidays || []) {
    const from = h && normalizeYMD(h.fromDate);
    const to = h && normalizeYMD(h.toDate);
    if (!from || !to) continue;
    let t = utcMidnightMs(from);
    const end = utcMidnightMs(to);
    let guard = 0;
    while (t <= end && guard < 400) {
      set.add(ymdFromUtcMs(t));
      t += DAY_MS;
      guard++;
    }
  }
  return set;
}

// Count working days (Mon–Fri, excluding holidays) between two dates, inclusive.
// `from`/`to` may be 'YYYY-MM-DD' strings or Date/ISO values.
export function countWorkingDaysBetween(from, to, holidaySet) {
  const fromYMD = normalizeYMD(from);
  const toYMD = normalizeYMD(to);
  if (!fromYMD || !toYMD) return 0;

  let t = utcMidnightMs(fromYMD);
  const end = utcMidnightMs(toYMD);
  if (t > end) return 0;

  const set = holidaySet instanceof Set ? holidaySet : new Set();
  let count = 0;
  let guard = 0;
  while (t <= end && guard < 4000) {
    const dow = new Date(t).getUTCDay(); // 0 = Sun … 6 = Sat
    if (dow !== 0 && dow !== 6) {
      if (!set.has(ymdFromUtcMs(t))) count++;
    }
    t += DAY_MS;
    guard++;
  }
  return count;
}
